import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { useFocusReturn } from '../lib/focus.ts';
import { Dialog as DialogRoot, DialogClose, DialogContent, DialogDescription, DialogTitle } from '../primitives/dialog.tsx';
import { IconButton } from './IconButton.tsx';

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** What this dialog is for. Required: it is the accessible name. */
  title: ReactNode;
  /** One line under the title. */
  description?: ReactNode;
  children?: ReactNode;
  /** Buttons, right-aligned; the primary action last. */
  footer?: ReactNode;
  /** sm 440, md 560 (default), lg and xl 720 (the widest a dialog gets). */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Hide the title visually (still read by screen readers). */
  hideTitle?: boolean;
  /** Prevent closing (Escape, outside click, the close button) while something is in flight. */
  dismissible?: boolean;
  className?: string;
  /** Element to focus first (defaults to the dialog itself). */
  initialFocus?: () => HTMLElement | null;
  bodyClassName?: string;
}

const SIZE = {
  sm: 'sm:max-w-(--dialog-width-sm)',
  md: 'sm:max-w-(--dialog-width)',
  lg: 'sm:max-w-(--dialog-width-lg)',
  xl: 'sm:max-w-(--dialog-width-lg)',
};

/**
 * A modal dialog for a short task, on the shadcn Dialog (components.md, Overlays): 560 wide,
 * the raised surface with the 12 px overlay radius and elevation 3 over a dimmed (never blurred)
 * backdrop. A 16/24 title, at most one short description line, the body, and a footer strip
 * with the buttons right-aligned and the one primary last. The body scrolls past
 * min(80vh, 640px), errors show inside and it stays open until the action succeeds. Closes with
 * Escape, a backdrop click and the close button. Never two at once.
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
  hideTitle,
  dismissible = true,
  className,
  bodyClassName,
  initialFocus,
}: DialogProps) {
  const block = (e: Event) => {
    if (!dismissible) e.preventDefault();
  };
  // Most dialogs open from state, not a Radix trigger: give focus back to what opened them.
  const focusReturn = useFocusReturn(open);
  return (
    <DialogRoot open={open} onOpenChange={(v) => (dismissible || v ? onOpenChange(v) : undefined)}>
      <DialogContent
        showCloseButton={false}
        onEscapeKeyDown={block}
        onInteractOutside={block}
        onOpenAutoFocus={(e) => {
          focusReturn.onOpenAutoFocus(e);
          // Focus the given element, else the dialog itself: never the close button, whose tooltip would pop open.
          e.preventDefault();
          const el = initialFocus?.() ?? (e.currentTarget as HTMLElement | null);
          el?.focus();
        }}
        onCloseAutoFocus={focusReturn.onCloseAutoFocus}
        tabIndex={-1}
        className={cn('flex flex-col gap-0 overflow-hidden p-0', SIZE[size], className)}
      >
        <div className={cn('flex items-start gap-3 px-(--dialog-padding) pt-(--dialog-padding)', hideTitle ? 'sr-only' : 'pb-4')}>
          <div className="min-w-0 flex-1">
            <DialogTitle className="pr-0">{title}</DialogTitle>
            {description ? (
              <DialogDescription className="mt-1 text-ui">{description}</DialogDescription>
            ) : (
              <DialogDescription className="sr-only">{typeof title === 'string' ? title : 'Dialog'}</DialogDescription>
            )}
          </div>
          <DialogClose asChild>
            <IconButton label="Close" icon={<X />} size="sm" className="-mr-2 -mt-0.5" shortcut={['esc']} disabled={!dismissible} />
          </DialogClose>
        </div>
        {children != null && (
          <div className={cn('max-h-[min(80vh,640px)] min-h-0 flex-1 overflow-y-auto px-(--dialog-padding) pb-(--dialog-padding)', bodyClassName)}>{children}</div>
        )}
        {footer && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border-subtle bg-sunken/50 px-(--dialog-padding) py-3">{footer}</div>
        )}
      </DialogContent>
    </DialogRoot>
  );
}

export { DialogClose };
